import ChevronIcon from '@/common/components/icons/chevron';
import { isSnappPaymentMethod } from '@/modules/booking/constants/extraWalletTopup';
import useResponsive from '@/common/hooks/useResponsive';
import haptic from '@/common/utils/haptics';
import { OPEN_INVOICE_EVENT } from '@/modules/booking/components/factor/invoice/invoice';
import { resolveRetryPaymentMethod } from '@/modules/booking/utils/retryPaymentMethod';
import { PENDING_PAYMENT_CHANGED_EVENT } from '@/modules/booking/components/pendingPaymentSheet/pendingPaymentSheet';
import PaymentProgress from '@/modules/booking/components/factor/paymentProgress';
import { clearPendingPayment, PendingPayment, readPendingPayment, savePendingPayment } from '@/modules/booking/utils/pendingPayment';
import Modal from '@/common/components/atom/modal';
import { Toman } from '@/common/components/atom/toman/toman';
import { useCenterPayment } from '@/common/apis/services/factor/centerPayment';
import { useConsultPayment } from '@/common/apis/services/factor/consultPayment';
import Alert from '@/common/components/atom/alert/alert';
import Button from '@/common/components/atom/button/button';
import Text from '@/common/components/atom/text/text';
import Skeleton from '@/common/components/atom/skeleton';
import { addCommas } from '@persian-tools/persian-tools';
import DiamondIcon from '@/common/components/icons/diamond';
import { CENTERS } from '@/common/types/centers';
import { checkPremiumUser } from '@/modules/bamdad/utils/checkPremiumUser';
import { useUserInfoStore } from '@/modules/login/store/userInfo';
import { useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { toast } from 'react-hot-toast';
import { CORPORATE_PAYMENT_METHOD } from '@/modules/corporate/components/factor';
import useDiscount from '../../hooks/factor/useDiscount';
import useInvoice from '../../hooks/factor/useInvoice';
import { useFactorActionGuard } from '../../hooks/factor/useFactorActionGuard';
import Factor from './factor';
import { useVpnPaymentHint } from '../../components/factor/vpnHint/vpnHint';
import { splunkInstance } from '@/common/services/splunk';
import { useLoginModalContext } from '@/modules/login/context/loginModal';
import useFirstFreeTime from '../../hooks/selectTime/useFirstFreeTime';
import useBooking from '../../hooks/booking';
import { growthbook } from 'src/pages/_app';
import { clearUxIssueContext, setUxIssueContext } from '@/common/services/rum/uxIssue';
import { useDiscountInquiry } from '@/common/apis/services/factor/discountInquiry';
import { encodeExtraWalletTopupPaymentMethod, tomanToRial } from '@/modules/booking/utils/encodeExtraWalletTopupPaymentMethod';
import {
  TOPUP_FIRST_FEATURE,
  TOPUP_RETURN_PARAM,
  TOPUP_UUID_PARAM,
  WALLET_PAYMENT_METHOD,
  buildTopupUrl,
  clearPendingTopup,
  computeTopupAmountRial,
  loadPendingTopup,
  savePendingTopup,
  stripTopupParams,
} from '@/modules/booking/utils/topupFirst';
import { useDoctorOnlineChannels } from '@/common/apis/services/doctor/getDoctorOnlineChannels';
import SelectUserWrapper from '@/modules/booking/views/selectUser/wrapper';
import { buildOvFailedUrl, buildOvFinishUrl, OV_REF_PARAM, readOvRef } from '@/modules/ovChat/rollout';
import Loading from '@/common/components/atom/loading/loading';
import { v4 as uuidV4 } from 'uuid';
import { setRumBook } from '@/common/services/rum/elastic/ids';

interface FactorWrapperProps {
  disabled?: boolean;
  bookId?: string;
  userCenterId?: string;
  serviceId?: string;
  centerId: string;
  respiteToRefundAfterDelete?: string;
  doctorName?: string;
  groupExpertise?: string;
  /** Doctor slug — top-up-first needs the doctor's online channels to book for the chosen patient. */
  doctorSlug?: string;
  /** The book row came back deleted (book_delete=1) — show the expired banner, no payment UI. */
  isBookDeleted?: boolean;
  /**
   * Ids known before the page's availability check (URL service id + the consult center cached from the
   * profile/search). Only used to start the price request early; actions use the confirmed ids.
   */
  earlyServiceId?: string;
  earlyUserCenterId?: string;
  /** A/B trust UI group (online-visit invoice only). */
  isTrustUi?: boolean;
}

const FactorWrapper = (props: FactorWrapperProps) => {
  const { isMobile } = useResponsive();
  const { bookId, centerId, serviceId, userCenterId, respiteToRefundAfterDelete, doctorName, groupExpertise, doctorSlug, isBookDeleted = false, disabled = false, earlyServiceId, earlyUserCenterId } = props;
  // A/B (decided once by the page, see useFactorTrustUiGroup): the trust UI's CTA only for its half.
  const isTrustUi = !!props.isTrustUi;
  const createActionGuard = useFactorActionGuard(disabled, `${centerId}/${userCenterId}/${serviceId}/${bookId}`);
  const centerPayment = useCenterPayment();
  const consultPayment = useConsultPayment();
  const userInfo = useUserInfoStore(state => state.info);
  const premiumOnlineVistDiscountCode = useFeatureValue('premium.online_visit_discount_code', '');
  const premiumOnlineVisitDiscountPercentage = useFeatureValue('premium.online_visit_discount_percentage', '');
  const useKatibePaymentForEarnestFactor = useFeatureIsOn('use-katibe-payment-for-earnest-factor');
  const isLogin = useUserInfoStore(state => state.isLogin);
  const loginModal = useLoginModalContext();
  const getFirstFreeTime = useFirstFreeTime({
    enabled: false,
    centerId: centerId,
    serviceId: serviceId!,
    serverId: '1',
    userCenterId: userCenterId!,
  });
  const { handleBook, isLoading: bookLoading } = useBooking();
  // «چت‌اول»: صفحه از گفتگوی حامی آمده (ov_ref). مسیر «اول پرداخت، بعد نوبت» برایش همیشه روشن است.
  const { query: routerQuery } = useRouter();
  const ovRef = readOvRef(routerQuery[OV_REF_PARAM]);
  // Top-up first (online visit, no booking yet): pay charges the wallet; booking runs after the return.
  const isTopupFirstOn = (useFeatureIsOn(TOPUP_FIRST_FEATURE) || !!ovRef) && centerId === CENTERS.CONSULT && !bookId;
  const doctorOnlineChannels = useDoctorOnlineChannels(doctorSlug, { enabled: isTopupFirstOn && !!doctorSlug });
  const [topupResume, setTopupResume] = useState<'idle' | 'select-user' | 'booking' | 'failed'>('idle');
  // Back from a successful top-up: hide the invoice before first paint so it never flashes.
  // Read from the router so the server-rendered HTML is hidden too (no flash before hydration).
  const [topupReturning, setTopupReturning] = useState(routerQuery[TOPUP_RETURN_PARAM] === 'success');
  useEffect(() => {
    if (!topupReturning) return;
    const fallback = setTimeout(() => setTopupReturning(false), 15_000);
    return () => clearTimeout(fallback);
  }, []);
  // Paid already — the invoice must not come back (patient choice, booking, retry). The return
  // params stay in the URL until booking succeeds, so a reload lands here again.
  const hideInvoice = topupResume !== 'idle' || topupReturning || isBookDeleted;

  // TEMP: measure how often factor users hit "no price" / booking errors. Remove after analysis.
  const logFactorIssue = (reason: string, extra: Record<string, unknown> = {}) => {
    try {
      splunkInstance('booking').sendEvent({
        group: 'FactorIssue',
        type: reason,
        keepalive: true,
        event: {
          center_id: centerId,
          service_id: serviceId,
          user_center_id: userCenterId,
          book_id: bookId,
          user_id: userInfo?.id,
          doctor_name: doctorName,
          ...extra,
        },
      });
    } catch {
      // Diagnostics must not interrupt payment.
    }
  };

  // What the request layer cannot know about a slow or failed booking call (see uxIssue.ts).
  // Must stay above useInvoice: effects run in this order, and the invoice request is the
  // first call of this page — it is only watched once the step is set.
  useEffect(() => {
    setUxIssueContext({
      step: 'FACTOR',
      doctor_name: doctorName,
      center_id: centerId,
      service_id: serviceId,
      user_center_id: userCenterId,
      book_id: bookId,
    });
    return clearUxIssueContext;
  }, [doctorName, centerId, serviceId, userCenterId, bookId]);

  const { isLoading, ...invoice } = useInvoice({
    bookId,
    centerId,
    serviceId: serviceId ?? earlyServiceId,
    userCenterId: userCenterId ?? earlyUserCenterId,
  });
  const { handleDiscountSubmit, ...discount } = useDiscount({
    bookId: bookId ?? '',
    centerId,
    serviceId,
    userCenterId: userCenterId,
  });
  const discountInquiry = useDiscountInquiry();
  const vpnHint = useVpnPaymentHint();

  // Deleted books are collected explicitly and immediately (once per book): their row
  // carries a price, so the 10s price_not_loaded gate would never see them. Not-found is
  // collected by the page — this component is unmounted in that state.
  const collectedTerminalBookRef = useRef<string>();
  useEffect(() => {
    if (!isLogin || !isBookDeleted || !bookId) return;
    const key = `factor_expired:${bookId}`;
    if (collectedTerminalBookRef.current === key) return;
    collectedTerminalBookRef.current = key;
    logFactorIssue('factor_expired', {
      has_user_center_id: !!userCenterId,
      ...invoice.priceDiagnostics,
    });
  }, [isLogin, isBookDeleted, bookId, invoice.priceDiagnostics]);

  // TEMP: price still missing 10s after login → invoice never loaded. The payload carries
  // the price-source diagnostics (which leg failed/slowed) so the alert is attributable.
  useEffect(() => {
    if (disabled || !isLogin || invoice.price) return;
    const timer = setTimeout(
      () =>
        logFactorIssue('price_not_loaded', {
          has_user_center_id: !!userCenterId,
          ...invoice.priceDiagnostics,
        }),
      10_000,
    );
    return () => clearTimeout(timer);
  }, [disabled, isLogin, invoice.price, userCenterId, serviceId, invoice.priceDiagnostics]);

  // Funnel start: the factor-page view, sent once the payable state is known (users whose
  // price never arrives are covered by the price_not_loaded gate above). The route uuid is
  // the doctor's SERVICE id — a book only exists after the pay action. keepalive + no await:
  // monitoring must never add a step the patient waits on.
  const factorMountedAtRef = useRef<number>();
  const factorViewSentRef = useRef<string>();
  useEffect(() => {
    if (disabled || !isLogin || !invoice.price) return;
    if (factorMountedAtRef.current === undefined) factorMountedAtRef.current = performance.now();
    const key = `${centerId}:${bookId}`;
    if (factorViewSentRef.current === key) return;
    factorViewSentRef.current = key;
    try {
      splunkInstance('booking').sendEvent({
        group: 'view-factor-page',
        type: 'view-factor-page',
        keepalive: true,
        event: {
          center_id: centerId,
          service_id: bookId,
          user_center_id: userCenterId ?? earlyUserCenterId,
          user_id: userInfo?.id,
          doctor_name: doctorName,
          has_price: !!invoice.price,
          time_to_price_ms:
            factorMountedAtRef.current !== undefined
              ? Math.round(performance.now() - factorMountedAtRef.current)
              : undefined,
        },
      });
    } catch {
      // Diagnostics must not interrupt payment.
    }
  }, [disabled, isLogin, invoice.price, centerId, bookId, userCenterId, earlyUserCenterId, userInfo?.id, doctorName]);
  const [payableAmount, setPayableAmount] = useState<number>();
  const [postLoginPaymentPending, setPostLoginPaymentPending] = useState(false);
  const [paymentActionPending, setPaymentActionPending] = useState(false);
  // Back from the gateway without paying: the reserved book is still held — continue on it.
  const [pendingPayment, setPendingPayment] = useState<PendingPayment>();
  const pendingPaymentUsed = useRef(false);
  const rememberGatewayTrip = (tripBookId?: string, method?: string) => {
    // RUM: the rest of this tab's events carry labels.book_id.
    setRumBook(tripBookId);
    // Online visit only (see shouldOpenPendingPaymentSheet): an in-person prepaid book keeps the old behaviour.
    if (tripBookId && centerId === CENTERS.CONSULT) savePendingPayment({ bookId: tripBookId, centerId, paymentMethod: method, factorPath: window.location.pathname, priceToman: payableAmount ? payableAmount / 10 : undefined });
  };
  const paymentRedirectStarted = useRef(false);
  const lastHapticStage = useRef<string>();
  const [paymentSheetHeight, setPaymentSheetHeight] = useState(420);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  const [extraWalletTopupEnabled, setExtraWalletTopupEnabled] = useState(false);

  useEffect(() => {
    setUxIssueContext({ has_price: !!invoice.price });
  }, [invoice.price, doctorName, centerId, serviceId, userCenterId, bookId]);
  // 0 = not set yet → first open uses visit total (with tax); later reopen uses last amount
  const [extraWalletTopupToman, setExtraWalletTopupToman] = useState(0);

  const resolvePaymentMethodForGateway = (paymentMethod?: string) => {
    const method = paymentMethod || selectedPaymentMethod;
    if (!method) return method;
    const extraRial = extraWalletTopupEnabled ? tomanToRial(extraWalletTopupToman) : 0;
    return encodeExtraWalletTopupPaymentMethod(method, extraRial);
  };

  const appendPaymentMethodToUrl = (url: string, paymentMethod?: string) => {
    if (!paymentMethod || !url) return url;

    try {
      const u = new URL(url, window.location.origin);
      // Literal `+` in payment_method (saman_topups+N) — avoid searchParams.set /
      // encodeURIComponent / URL.toString() re-encoding (+ → %2B); topups accepts bare +.
      u.searchParams.delete('payment_method');
      const rest = u.searchParams.toString();
      const pmQuery = `payment_method=${paymentMethod}`;
      const search = rest ? `?${rest}&${pmQuery}` : `?${pmQuery}`;
      if (url.startsWith('http')) {
        return `${u.origin}${u.pathname}${search}${u.hash}`;
      }
      return `${u.pathname}${search}${u.hash}`;
    } catch {
      const [base, hash = ''] = url.split('#');
      const withoutPm = base.replace(/([?&])payment_method=[^&]*(&?)/, (_, p1, p2) => (p2 ? p1 : '')).replace(/[?&]$/, '');
      const joiner = withoutPm.includes('?') ? '&' : '?';
      const withQuery = `${withoutPm}${joiner}payment_method=${paymentMethod}`;
      return hash ? `${withQuery}#${hash}` : withQuery;
    }
  };

  const [paymentMethodSelectedByUser, setPaymentMethodSelectedByUser] = useState(false);
  const handlePaymentMethodSelection = (paymentMethod: string, byUser = false) => {
    setSelectedPaymentMethod(paymentMethod);
    setPaymentMethodSelectedByUser(byUser);
  };

  useEffect(() => {
    growthbook.setAttributes({
      ...growthbook.getAttributes(),
      center_id: centerId,
      ...(serviceId && { service_id: serviceId }),
      ...(userCenterId && { user_center_id: userCenterId }),
    });
  }, [userCenterId, serviceId, centerId]);

  const isApplyPremiumDiscount = useMemo(
    () =>
      checkPremiumUser(userInfo.vip) &&
      centerId === CENTERS.CONSULT &&
      premiumOnlineVistDiscountCode &&
      premiumOnlineVisitDiscountPercentage,
    [userInfo, premiumOnlineVistDiscountCode, premiumOnlineVisitDiscountPercentage, centerId],
  );

  useEffect(() => {
    if (!disabled && isApplyPremiumDiscount) {
      handleDiscountSubmit(premiumOnlineVistDiscountCode);
    }
  }, [disabled, isApplyPremiumDiscount]);

  const performPaymentAction = async ({
    discountToken,
    bookId,
    paymentMethod,
    userDataFromLogin = null,
    discountCode,
    fromWallet = false,
    patient,
  }: {
    discountToken?: string;
    bookId?: string;
    paymentMethod?: string;
    userDataFromLogin?: any;
    /** Discount code to apply when booking (discount state is lost across the top-up redirect). */
    discountCode?: string;
    /** Book and pay from the wallet — used after a successful top-up return. */
    fromWallet?: boolean;
    /** Patient picked after the top-up return — books directly for them (no finalize step). */
    patient?: any;
  }): Promise<unknown> => {
    const canProceed = createActionGuard();
    if (!canProceed()) return;
    // Paying from the wallet needs no gateway trip — keep the direct book-and-pay flow.
    const isWalletSelected = String(paymentMethod || selectedPaymentMethod || '')
      .toLowerCase()
      .startsWith(WALLET_PAYMENT_METHOD);
    if (isTopupFirstOn && !bookId && serviceId && !fromWallet && !isWalletSelected) {
      // Invoice payable: service price + tax − discount — everything on the invoice.
      const totalRial = Number(discount.totalPrice ?? invoice.totalPrice);
      if (!Number.isFinite(totalRial) || totalRial <= 0) {
        logFactorIssue('topup_no_price');
        return toast.error('مبلغ قابل پرداخت مشخص نیست. لطفاً صفحه را دوباره باز کنید.');
      }
      const extraRial = extraWalletTopupEnabled ? tomanToRial(extraWalletTopupToman) : 0;
      const amountRial = computeTopupAmountRial(totalRial, extraRial);
      const code = discount?.isValidDiscount ? discount.code : undefined;
      const uuid = uuidV4();
      savePendingTopup({
        uuid,
        centerId,
        serviceId,
        userCenterId: userCenterId!,
        requiredRial: totalRial,
        discountCode: code,
        createdAt: Date.now(),
      });
      splunkInstance('booking').sendEvent({
        group: 'FinancialTransactions',
        type: 'TopupFirstStarted',
        event: { center_id: centerId, user_id: userInfo.id, doctor_name: doctorName, amount: amountRial, total: totalRial },
      });
      haptic('success');
      paymentRedirectStarted.current = true;
      location.assign(
        buildTopupUrl({
          amountRial,
          uuid,
          pageUrl: stripTopupParams(window.location.href),
          centerId,
          paymentMethod: paymentMethod || selectedPaymentMethod,
          taxRial: Number(discount.tax ?? invoice.tax) || 0,
          discountRial: Number(discount.discount) || 0,
        }),
      );
      return;
    }
    const gatewayPaymentMethod = fromWallet ? WALLET_PAYMENT_METHOD : resolvePaymentMethodForGateway(paymentMethod);
    if (serviceId || bookId) {
      if (!bookId) {
        let reserveId: string | undefined;

        const freeturnData = await getFirstFreeTime.getFirstFreeTime();
        if (!canProceed()) return;
        if (!freeturnData.timeId) {
          logFactorIssue('free_turn_error', { message: freeturnData?.message, has_price: !!invoice.price });
          return toast.error(freeturnData?.message ?? 'خطا در دریافت نوبت خالی پزشک.');
        }
        reserveId = freeturnData.timeId;

        return handleBook(
          {
            center: {
              id: centerId,
              user_center_id: userCenterId!,
              server_id: '1',
            },
            timeId: reserveId as string,
            user: patient
              ? { ...patient, messengerType: (doctorOnlineChannels.data ?? []).includes('whatsapp') ? 'hami' : 'eitaa' }
              : {
                  name: '',
                  family: '',
                  cell: userDataFromLogin?.cell ?? userInfo?.cell,
                  messengerType: 'skip_select_user',
                },
            paymentMethod: gatewayPaymentMethod,
          },
          {
            async onSuccess(data) {
              if (!canProceed()) return;
              if (data.payment.reqiure_payment === '1') {
                let discountToken;
                const bookDiscountCode = discountCode ?? (discount?.isValidDiscount ? discount?.code : undefined);
                if (bookDiscountCode) {
                  const discountInquiryForToken = await discountInquiry.mutateAsync({
                    book_id: data?.book_info?.id,
                    centerId,
                    serviceId,
                    userCenterId,
                    code: bookDiscountCode,
                  });
                  discountToken = discountInquiryForToken?.data.result?.token;
                }
                if (!canProceed()) return;
                const { data: paymentData } = await [centerId === CENTERS.CONSULT ? consultPayment : centerPayment][0].mutateAsync({
                  book_id: data?.book_info?.id as string,
                  ...(discountToken && { discount_token: discountToken }),
                  ...(gatewayPaymentMethod && { payment_method: gatewayPaymentMethod }),
                });
                if (!canProceed()) return;
                if (paymentData.status) {
                  splunkInstance('booking').sendEvent({
                    group: 'FinancialTransactions',
                    type: 'PaymentButtonClicked',
                    // The redirect below races this POST — keepalive lets it survive the unload.
                    keepalive: true,
                    event: {
                      book_id: data?.book_info?.id,
                      center_id: centerId,
                      user_id: userInfo.id,
                      doctor_name: doctorName,
                      group_expertise: groupExpertise,
                      payment_method: paymentMethod || selectedPaymentMethod,
                      is_default_payment_method: !paymentMethodSelectedByUser,
                    },
                  });
                  haptic('success');
        paymentRedirectStarted.current = true;
                  window?.clarity?.('event', 'factor_gateway_redirect');
                  rememberGatewayTrip(data?.book_info?.id as string, gatewayPaymentMethod);
                  // «چت‌اول»: بعد از ثبت نوبت از کیف پول، بیمار به گفتگوی حامی برمی‌گردد تا گفتگو برای پزشک باز شود
                  if (ovRef && fromWallet && data?.book_info?.id) {
                    location.assign(buildOvFinishUrl(ovRef, data.book_info.id));
                    return;
                  }
                  location.assign(appendPaymentMethodToUrl(paymentData.url, gatewayPaymentMethod));
                  //location.assign(paymentData.url);
                  return;
                }
                logFactorIssue('payment_error', { message: paymentData?.message });
                toast.error(paymentData?.message ?? 'یک خطای غیرمنتظره رخ داد.');
              }
            },
            onError(data) {
              logFactorIssue('book_error', { message: data?.message, has_price: !!invoice.price });
              if (Object.values(data?.details ?? {})?.length > 0) {
                toast.error(
                  `${data.message} \n ${Object.entries(data?.details)
                    .map(item => `${item[0]}: ${item[1]}`)
                    .join('\n')}`,
                  {
                    duration: 10000,
                  },
                );
              }
              if (Object.values(data?.details ?? {})?.length == 0) {
                toast.error(data.message, {
                  duration: 10000,
                });
              }
            },
          },
        );
      }

      const { data } = await [centerId === CENTERS.CONSULT ? consultPayment : centerPayment][0].mutateAsync({
        book_id: bookId,
        ...(discountToken && { discount_token: discountToken }),
        ...(gatewayPaymentMethod && { payment_method: gatewayPaymentMethod }),
      });
      if (!canProceed()) return;
      if (data.status) {
        splunkInstance('booking').sendEvent({
          group: 'FinancialTransactions',
          type: 'PaymentButtonClicked',
          // The redirect below races this POST — keepalive lets it survive the unload.
          keepalive: true,
          event: {
            book_id: bookId,
            center_id: centerId,
            user_id: userInfo.id,
            doctor_name: doctorName,
            group_expertise: groupExpertise,
            payment_method: paymentMethod || selectedPaymentMethod,
            is_default_payment_method: !paymentMethodSelectedByUser,
          },
        });
        haptic('success');
        paymentRedirectStarted.current = true;
        window?.clarity?.('event', 'factor_gateway_redirect');
        rememberGatewayTrip(bookId, gatewayPaymentMethod);
        location.assign(appendPaymentMethodToUrl(data.url, gatewayPaymentMethod));
        //location.assign(data.url);
        return;
      }
      logFactorIssue('payment_error', { message: data?.message });
      toast.error(data?.message ?? 'یک خطای غیرمنتظره رخ داد.');
    }
  };

  // Nothing left to pay (discount/wallet) → no bank gateway → no VPN hint.
  const payViaGateway = (params: Parameters<typeof performPaymentAction>[0]) =>
    payableAmount === 0 ? handlePaymentAction(params) : vpnHint.guard(() => handlePaymentAction(params));

  // Back from the gateway restores this page from the bfcache with the payment sheet still open —
  // the redirect never finished from this page's point of view, so reset the pending state.
  useEffect(() => {
    // The global sheet tells the patient; here the pay button just continues on the held book.
    const checkPendingPayment = () => {
      const pending = readPendingPayment();
      setPendingPayment(pending?.factorPath === window.location.pathname ? pending : undefined);
    };
    checkPendingPayment();
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) checkPendingPayment();
      if (!event.persisted || !paymentRedirectStarted.current) return;
      paymentRedirectStarted.current = false;
      setPaymentActionPending(false);
      setPostLoginPaymentPending(false);
    };
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener(PENDING_PAYMENT_CHANGED_EVENT, checkPendingPayment);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener(PENDING_PAYMENT_CHANGED_EVENT, checkPendingPayment);
    };
  }, []);

  const handlePaymentAction = async (params: Parameters<typeof performPaymentAction>[0]) => {
    if (paymentActionPending) return;
    if (!params.bookId && pendingPayment) {
      // Same bank failed a moment ago — continue on another bank when one is offered.
      const retryMethod =
        params.paymentMethod === pendingPayment.paymentMethod ? await resolveRetryPaymentMethod(params.paymentMethod, centerId, payableAmount) : params.paymentMethod;
      params = { ...params, bookId: pendingPayment.bookId, paymentMethod: retryMethod };
      pendingPaymentUsed.current = true;
    }
    const doctorCard = document.querySelector('[data-factor-doctor-notice]');
    const cardBottom = doctorCard?.getBoundingClientRect().bottom ?? 180;
    setPaymentSheetHeight(Math.max(240, window.innerHeight - Math.max(16, cardBottom) - 24));
    paymentRedirectStarted.current = false;
    haptic('impact');
    lastHapticStage.current = 'checking';
    setPaymentActionPending(true);
    try {
      return await performPaymentAction(params);
    } catch (error) {
      paymentRedirectStarted.current = false;
      const message = (error as any)?.response?.data?.message ?? (error as any)?.message;
      toast.error(message || 'خطا در آماده‌سازی پرداخت. لطفاً دوباره تلاش کنید.');
    } finally {
      if (!paymentRedirectStarted.current) {
        setPaymentActionPending(false);
        // The held book could not be paid (expired, already settled…) — next tap starts fresh.
        if (pendingPaymentUsed.current) {
          clearPendingPayment();
          setPendingPayment(undefined);
        }
      }
      pendingPaymentUsed.current = false;
    }
  };
  const paymentPending = paymentActionPending || postLoginPaymentPending || bookLoading || getFirstFreeTime.loading || centerPayment.isLoading || consultPayment.isLoading || discountInquiry.isLoading;
  const paymentStage = getFirstFreeTime.loading
    ? 'در حال بررسی نوبت…'
    : bookLoading
      ? 'در حال رزرو نوبت…'
      : 'در حال آماده‌سازی پرداخت…';

  useEffect(() => {
    if (!paymentPending) {
      lastHapticStage.current = undefined;
      return;
    }
    const stage = bookLoading ? 'reserving' : centerPayment.isLoading || consultPayment.isLoading || discountInquiry.isLoading ? 'payment' : 'checking';
    if (stage !== lastHapticStage.current && stage !== 'checking') haptic('selection');
    lastHapticStage.current = stage;
  }, [paymentPending, bookLoading, centerPayment.isLoading, consultPayment.isLoading, discountInquiry.isLoading]);

  // Back from the top-up gateway: book and pay from the wallet.
  const topupResumeStarted = useRef(false);
  const topupDiscountCode = useRef<string>();
  const topupPendingUuid = useRef<string>();
  const topupPatient = useRef<any>();
  const bookFromWallet = async (patient?: any) => {
    if (patient) topupPatient.current = patient;
    if (!topupPatient.current) return setTopupResume('select-user');
    const pending = topupPendingUuid.current ? loadPendingTopup(topupPendingUuid.current) : null;
    if (pending) savePendingTopup({ ...pending, bookingStarted: true });
    setTopupResume('booking');
    paymentRedirectStarted.current = false;
    await handlePaymentAction({ discountCode: topupDiscountCode.current, fromWallet: true, patient: topupPatient.current });
    clearPendingTopup();
    if (!paymentRedirectStarted.current) setTopupResume('failed');
  };
  useEffect(() => {
    if (!isTopupFirstOn || topupResumeStarted.current || typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const status = params.get(TOPUP_RETURN_PARAM);
    const uuid = params.get(TOPUP_UUID_PARAM);
    if (!status || !uuid || !isLogin || !serviceId || !userCenterId) return;
    topupResumeStarted.current = true;
    const pending = loadPendingTopup(uuid);
    splunkInstance('booking').sendEvent({
      group: 'FinancialTransactions',
      type: 'TopupFirstReturned',
      event: { center_id: centerId, user_id: userInfo.id, doctor_name: doctorName, status, has_pending: !!pending },
    });
    if (status !== 'success') {
      window.history.replaceState(window.history.state, '', stripTopupParams(window.location.href));
      setTopupReturning(false);
      clearPendingTopup();
      toast.error('پرداخت انجام نشد.');
      return;
    }
    // Missing/used pending (reload, other tab): never book twice — the money stays in the wallet.
    if (!pending || pending.bookingStarted || pending.serviceId !== serviceId) {
      clearPendingTopup();
      setTopupResume('failed');
      return;
    }
    topupDiscountCode.current = pending.discountCode;
    topupPendingUuid.current = uuid;
    // Booking waits for the patient choice; the slow services run after it.
    setTopupResume('select-user');
  }, [isTopupFirstOn, isLogin, serviceId, userCenterId]);

  const getRules = () => {
    if (centerId === CENTERS.CONSULT) return [];
    return [
      `تنها در صورت لغو نوبت تا <b> ${respiteToRefundAfterDelete} ساعت</b> قبل از زمان ویزیت، امکان استرداد وجه شما ممکن می باشد.`,
      !useKatibePaymentForEarnestFactor
        ? `این مبلغ هزینه ویزیت شما است که به‌صورت آنلاین پرداخت می‌شود.`
        : `مبلغ فوق به عنوان پیش پرداخت حق ویزیت (بیعانه) می باشد و تسویه نهایی بعد از مراجعه به مطب انجام خواهد شد.`,
      !useKatibePaymentForEarnestFactor
        ? `درصورتی‌که خدمات اضافی در مطب ارائه شود، ممکن است هزینه‌های دیگری نیز توسط پزشک تعیین و دریافت شود.`
        : undefined,
    ];
  };

  return (
    <div className="flex flex-col w-full space-y-6">
      {isBookDeleted && (
        <Alert severity="warning" className="p-4 flex flex-col gap-1">
          <Text fontWeight="bold">این فاکتور منقضی شده است.</Text>
          <Text fontSize="sm">نوبت موردنظر حذف شده و امکان پرداخت آن وجود ندارد.</Text>
        </Alert>
      )}
      <fieldset disabled={disabled} className={`min-w-0 w-full ${hideInvoice ? 'hidden' : ''}`}>
      <Factor
        {...invoice}
        {...discount}
        centerId={centerId}
        bookId={bookId ?? ''}
        loading={isLoading || !invoice.price}
        onSubmitDiscount={(code: string) => {
          const canProceed = createActionGuard();
          if (!canProceed()) return;
          if (!isLogin) {
            return loginModal.handleOpenLoginModal({
              state: true,
              postLogin: userInfo => { if (canProceed()) void handleDiscountSubmit(code); },
            });
          }
          handleDiscountSubmit(code);
        }}
        onPayment={handlePaymentAction}
        onPayableAmountChange={setPayableAmount}
        selectedPaymentMethod={selectedPaymentMethod}
        onSelectionChange={handlePaymentMethodSelection}
        extraWalletTopupEnabled={extraWalletTopupEnabled}
        extraWalletTopupToman={extraWalletTopupToman}
        onExtraWalletTopupEnabledChange={setExtraWalletTopupEnabled}
        onExtraWalletTopupTomanChange={setExtraWalletTopupToman}
        isShowDiscountInput={
          centerId === CENTERS.CONSULT &&
          (checkPremiumUser(userInfo.vip) ? !premiumOnlineVisitDiscountPercentage && !premiumOnlineVistDiscountCode : true)
        }
        rules={getRules()?.filter(Boolean) as string[]}
      />
      </fieldset>
      {topupReturning && topupResume === 'idle' && (
        <div className="flex justify-center p-10" role="status" aria-live="polite">
          <Loading />
        </div>
      )}
      {topupResume === 'select-user' && (
        <div className="flex flex-col gap-3 p-4 bg-white rounded-lg shadow-card">
          <Text fontWeight="bold">لطفا بیمار را انتخاب کنید</Text>
          <SelectUserWrapper
            loading={doctorOnlineChannels.isLoading}
            onSubmit={(patient: any) => void bookFromWallet(patient)}
            shouldShowMessengers={false}
            submitButtonText="ثبت نوبت و آغاز گفت‌وگو"
            showTermsAndConditions={false}
          />
        </div>
      )}
      {topupResume === 'failed' && (
        <Alert severity="warning" className="p-4 flex flex-col gap-3">
          <Text fontSize="sm" fontWeight="medium" className="text-amber-800">
            پرداخت شما انجام شد و مبلغ در کیف پول‌تان محفوظ است، اما ثبت نوبت کامل نشد.
          </Text>
          <Button size="sm" variant="secondary" onClick={() => void bookFromWallet()} loading={paymentPending}>
            تلاش دوباره برای ثبت نوبت
          </Button>
          {ovRef && (
            <Button size="sm" variant="secondary" onClick={() => location.assign(buildOvFailedUrl(ovRef))}>
              بازگشت به گفتگو
            </Button>
          )}
        </Alert>
      )}
      {isApplyPremiumDiscount && (
        <Alert severity="warning" className="p-4">
          <div className="flex items-center space-s-2 text-amber-700">
            <DiamondIcon />
            <Text fontSize="sm" fontWeight="medium">
              %{premiumOnlineVisitDiscountPercentage} تخفیف برای ویزیت آنلاین اعمال شده است.
            </Text>
          </div>
        </Alert>
      )}
      <Modal
        isOpen={(paymentPending && isMobile && centerId === CENTERS.CONSULT && !(isTopupFirstOn && !String(selectedPaymentMethod || '').toLowerCase().startsWith(WALLET_PAYMENT_METHOD))) || topupResume === 'booking'}
        onClose={() => undefined}
        name="factor-payment-progress"
        dismissible={false}
        noHeader
        noLine
        bodyClassName="!p-0"
      >
        <div className="flex flex-col items-center justify-center gap-4 px-5 md:!h-[320px]" style={{ height: paymentSheetHeight }} role="status" aria-live="polite" aria-busy="true">
          <PaymentProgress stage={topupResume === 'booking' ? 'checking' : bookLoading ? 'reserving' : centerPayment.isLoading || consultPayment.isLoading || discountInquiry.isLoading ? 'payment' : 'checking'} />
          <p className="text-sm font-semibold text-slate-900">{topupResume === 'booking' ? 'در حال ثبت نوبت…' : paymentStage}</p>
        </div>
      </Modal>
      {vpnHint.sheet}
      <div className={`fixed bottom-0 right-0 z-30 flex items-center justify-between gap-6 w-full border-t border-slate-200 md:border-t-0 px-4 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))] bg-slate-50 md:p-5 md:static md:rounded-lg lg:!bg-transparent lg:!p-0 lg:!rounded-none shadow-[0_-6px_20px_rgba(23,23,23,0.16)] md:shadow-none ${hideInvoice ? 'hidden' : ''}`}>
        <button
          type="button"
          onClick={() => window.dispatchEvent(new Event(OPEN_INVOICE_EVENT))}
          aria-label="نمایش جزئیات پرداخت"
          className={`flex shrink-0 flex-col items-start gap-1 text-start lg:hidden ${disabled ? 'invisible' : ''}`}
          aria-live="polite"
          aria-atomic="true"
        >
          <span className="text-xs text-slate-500">مبلغ قابل پرداخت</span>
          {isLoading || discount.discountLoading || payableAmount === undefined ? (
            <Skeleton w="6rem" h="1.5rem" rounded="sm" />
          ) : (
            <span className="inline-flex items-center gap-1 text-slate-900">
              <strong className="text-base sm:text-lg font-bold tabular-nums">{addCommas(Math.round(payableAmount / 10))}</strong>
              <Toman className="text-xs" />
            </span>
          )}
        </button>
        <Button
          disabled={disabled || (isLogin && (isLoading || discount.discountLoading || payableAmount === undefined))}
          onClick={() => {
            // «ویزیت آنلاین سازمانی» is shown to one account but not wired to a backend yet: nothing is booked.
            if (selectedPaymentMethod === CORPORATE_PAYMENT_METHOD) {
              toast.error('پرداخت با اعتبار سازمانی هنوز فعال نیست. لطفاً روش پرداخت دیگری انتخاب کنید.');
              return;
            }
            const canProceed = createActionGuard();
            if (!canProceed()) return;
            if (centerId === CENTERS.CONSULT && typeof window !== 'undefined') {
              window.pishani?.sendEvent?.('CLICK', {
                action: 'consult_payment_start_chat',
                center_id: centerId,
                user_id: userInfo?.id,
                book_id: bookId ?? '',
              });
            }
            if (!isLogin) {
              return loginModal.handleOpenLoginModal({
                state: true,
                postLogin: async userInfo => {
                  if (!canProceed()) return;
                  setPostLoginPaymentPending(true);
                  try {
                    await payViaGateway({
                    discountToken: discount.discountToken,
                    bookId: bookId ?? '',
                    paymentMethod: selectedPaymentMethod,
                    userDataFromLogin: userInfo,
                    });
                  } finally {
                    setPostLoginPaymentPending(false);
                  }
                },
              });
            }
            payViaGateway({
              discountToken: discount.discountToken,
              bookId: bookId ?? '',
              paymentMethod: selectedPaymentMethod,
            });
          }}
          className="flex-1 max-w-[65%] lg:flex-none lg:!w-[220px] lg:max-w-none lg:mr-auto !w-auto !px-4 !text-sm !font-semibold min-h-[48px]"
          loading={(isTopupFirstOn && (paymentActionPending || topupResume === 'booking')) || postLoginPaymentPending || centerPayment.isLoading || consultPayment.isLoading || bookLoading || getFirstFreeTime?.loading || (isLogin && (isLoading || discount.discountLoading || payableAmount === undefined))}
        >
          {pendingPayment
            ? 'ادامهٔ پرداخت'
            : selectedPaymentMethod === CORPORATE_PAYMENT_METHOD
            ? 'شروع ویزیت'
            : centerId !== CENTERS.CONSULT
            ? 'پرداخت و ثبت نوبت'
            : !isTrustUi
              ? 'پرداخت و آغاز گفت‌وگو'
              : // Snapp Pay is "visit now, pay later" — nothing is paid at this tap.
                selectedPaymentMethod && isSnappPaymentMethod(selectedPaymentMethod)
                ? 'شروع ویزیت'
                : 'پرداخت و شروع ویزیت'}
          {/* Forward chevron (points left in RTL), like the profile's «شروع ویزیت آنلاین» button. */}
          {isTrustUi && <ChevronIcon dir="left" className="ms-2" aria-hidden="true" />}
        </Button>
      </div>
    </div>
  );
};

export default FactorWrapper;

